import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../shared/components';
import { Filter, X, Users, Calendar, Code, Building2, CalendarRange } from 'lucide-react';
import { eventService } from '../../../shared/services';
import { buildYearOptions, getCurrentYear, filterActiveEvents } from '../../../shared/utils/eventStatus';

/**
 * Componente de filtros para Alumnos
 * Permite filtrar por nombre, evento, estado y sede
 */
const StudentsFilters = ({
  filters = {},
  onFiltersChange,
  onClearFilters,
  onSearch,
  searchPlaceholder = 'Buscar por nombre del Alumno...',
  loading = false,
  onEventsLoaded,
}) => {
  // Estado local para los inputs
  const [localSearch, setLocalSearch] = useState(filters.search || '');
  const [localEventId, setLocalEventId] = useState(filters.eventId || 'all');
  const [localStatus, setLocalStatus] = useState(filters.status || 'all');
  const [localAccessCode, setLocalAccessCode] = useState(filters.accessCode || '');
  const [localYear, setLocalYear] = useState(filters.year || getCurrentYear());

  // Estado para eventos disponibles
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const yearOptions = buildYearOptions(events);
  
  // Flag para evitar interferencia durante la aplicación de filtros
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  // Sincronizar estado local cuando cambian los filtros externos
  // Solo sincronizar si no estamos aplicando filtros activamente
  useEffect(() => {
    if (!isApplyingFilters) {
      setLocalSearch(filters.search || '');
      setLocalEventId(filters.eventId || 'all');
      setLocalStatus(filters.status || 'all');
      setLocalAccessCode(filters.accessCode || '');
      setLocalYear(filters.year || getCurrentYear());
    }
  }, [filters.search, filters.eventId, filters.status, filters.accessCode, filters.year, isApplyingFilters]);

  // Cargar lista de eventos para el filtro
  const loadEvents = useCallback(async () => {
    try {
      setEventsLoading(true);
      const eventsData = await eventService.getEvents();
      setEvents(filterActiveEvents(eventsData));
      if (typeof onEventsLoaded === 'function') {
        onEventsLoaded(eventsData);
      }
    } catch (error) {
      console.error('Error al cargar eventos:', error);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [onEventsLoaded]);

  // Cargar eventos al montar el componente (después de definir loadEvents)
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleClearFilters = () => {
    setLocalSearch('');
    setLocalEventId('all');
    setLocalStatus('all');
    setLocalAccessCode('');
    setLocalYear(getCurrentYear());
    onClearFilters();
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
  };

  const handleEventChange = (e) => {
    const value = e.target.value;
    setLocalEventId(value);
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    setLocalStatus(value);
  };

  const handleAccessCodeChange = (e) => {
    const value = e.target.value;
    setLocalAccessCode(value);
  };

  const handleApplyFilters = () => {
    // Marcar que estamos aplicando filtros para evitar interferencia
    setIsApplyingFilters(true);
    
    // Usar directamente los valores locales para evitar problemas de sincronización
    const newFilters = {
      search: localSearch,
      eventId: localEventId,
      status: localStatus,
      accessCode: localAccessCode,
      year: localYear,
      applyNow: true
    };
    
    // Primero actualizar los filtros
    onFiltersChange(newFilters);
    
    // Luego ejecutar la búsqueda con los filtros actualizados
    // Pasar los filtros directamente para evitar problemas de sincronización
    onSearch(newFilters);
    
    // Resetear el flag después de un breve delay para permitir que se procesen los cambios
    setTimeout(() => {
      setIsApplyingFilters(false);
    }, 100);
  };

  // Formatear para mostrar: title - time (HH:mm)
  const formatEventDisplay = (event) => {
    if (!event) return '';
    const time = (event.eventTime || event.time || '').toString();
    const hhmm = time ? time.slice(0,5) : '';
    return hhmm ? `${event.title} - ${hhmm}` : event.title;
  };

  const hasActiveFilters =
    localSearch !== '' ||
    localEventId !== 'all' ||
    localStatus !== 'all' ||
    localAccessCode !== '' ||
    localYear !== getCurrentYear();

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-600" />
          Filtros de Alumnos
        </h3>
        {hasActiveFilters && (
          <Button
            onClick={handleClearFilters}
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
          >
            <X className="w-4 h-4 mr-1" />
            Limpiar Filtros
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        {/* Búsqueda por nombre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            Buscar Alumno
          </label>
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={localSearch}
            onChange={handleSearchChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            disabled={loading}
          />
        </div>

        {/* Filtro por evento */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Evento
          </label>
          <select
            value={localEventId}
            onChange={handleEventChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
            disabled={loading || eventsLoading}
          >
            <option value="all">Todos los eventos</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {formatEventDisplay(event)}
              </option>
            ))}
          </select>
        </div>

        {/* Filtro por año */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <CalendarRange className="w-4 h-4 inline mr-1" />
            Año
          </label>
          <select
            value={localYear}
            onChange={(e) => setLocalYear(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
            disabled={loading || eventsLoading}
          >
            <option value="all">Todos los años</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Filtro por código de acceso */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Code className="w-4 h-4 inline mr-1" />
            Código de Acceso
          </label>
          <input
            type="text"
            placeholder="Buscar por código..."
            value={localAccessCode}
            onChange={handleAccessCodeChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            disabled={loading}
          />
        </div>

        {/* Filtro por estado */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building2 className="w-4 h-4 inline mr-1" />
            Estado
          </label>
          <select
            value={localStatus}
            onChange={handleStatusChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
            disabled={loading}
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Botón de aplicar filtros */}
      <div className="flex justify-end">
        <Button
          onClick={handleApplyFilters}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
        >
          {loading ? 'Aplicando...' : 'Aplicar Filtros'}
        </Button>
      </div>
    </div>
  );
};

export default StudentsFilters;